import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import i18n from '../i18n';
import { toLatLng, worldRectToBounds, xzToLatLng } from '../geo/crs';
import { exactCalibration, imageWorldBounds, worldToPixel } from '../geo/calibration';
import { geometryAnchor, polygonArea } from '../geo/measure';
import { unityInspectorVector3 } from '../geo/format';
import type { Geometry, XZ } from '../geo/types';
import type { FeatureRecord } from '../lib/types';
import { protectedFileUrl } from '../lib/pb';
import { featureVisible, getState, setState, toast, useStore } from '../store/store';
import { createFromPreset, updateFeature } from '../store/actions';
import { sampleHeight } from '../store/data';
import { useCalibration } from '../admin/calibrationStore';
import { useCursor, useMeasure } from './cursor';
import { buildLayer, geometryFromLayer, geomanShape } from './layers';

type Dispose = () => void;

// Leaflet-Geoman augments layers/maps at runtime; these are the few members we touch.
type PmLayer = L.Layer & {
  pm?: { enable: (o?: object) => void; disable: () => void };
};
type PmMap = L.Map & {
  pm: {
    enableDraw: (shape: string, o?: object) => void;
    disableDraw: () => void;
    setGlobalOptions: (o: object) => void;
  };
};

const PANES: [string, number][] = [
  ['base', 200],
  ['grid', 250],
  ['zones', 400],
  ['areas', 410],
  ['paths', 420],
  ['asbuilt', 430],
  ['markers', 610],
];

export function createPanes(lmap: L.Map) {
  for (const [name, z] of PANES) {
    const p = lmap.createPane(name);
    p.style.zIndex = String(z);
  }
  lmap.getPane('grid')!.style.pointerEvents = 'none';
  lmap.getPane('asbuilt')!.style.pointerEvents = 'none';
}

// ── base layers ─────────────────────────────────────────────────────────────────────────────

function baseLayersController(lmap: L.Map): Dispose {
  const overlays = new Map<string, { overlay: L.ImageOverlay; image: string }>();
  let queue: Promise<void> = Promise.resolve();
  let disposed = false;

  const sync = async () => {
    const s = getState();
    const cal = useCalibration.getState();
    const seen = new Set<string>();
    for (const l of s.baseLayers) {
      if (l.kind !== 'image' || !l.image || !l.widthPx || !l.heightPx) continue;
      seen.add(l.id);
      const view = s.layerView[l.id] ?? { visible: l.visibleByDefault, opacity: l.opacity || 1 };
      let calibration = cal.layerId === l.id && cal.draft ? cal.draft : l.calibration;
      if (!calibration) {
        // Uncalibrated: stretch the image over the whole world rect.
        const map = s.maps.find((m) => m.id === s.mapId);
        if (!map) continue;
        calibration = exactCalibration(map.worldRect, l.widthPx, l.heightPx);
      }
      const bounds = worldRectToBounds(imageWorldBounds(calibration, l.widthPx, l.heightPx));
      let entry = overlays.get(l.id);
      if (!entry || entry.image !== l.image) {
        entry?.overlay.remove();
        const url = await protectedFileUrl(l, l.image);
        if (disposed) return;
        const overlay = L.imageOverlay(url, bounds, {
          pane: 'base',
          opacity: view.opacity,
          interactive: false,
          className: 'base-img',
          pmIgnore: true,
        } as L.ImageOverlayOptions);
        entry = { overlay, image: l.image };
        overlays.set(l.id, entry);
      } else {
        entry.overlay.setBounds(bounds);
        entry.overlay.setOpacity(view.opacity);
      }
      entry.overlay.setZIndex(l.sortOrder || 0);
      if (view.visible && !lmap.hasLayer(entry.overlay)) entry.overlay.addTo(lmap);
      if (!view.visible && lmap.hasLayer(entry.overlay)) entry.overlay.remove();
    }
    for (const [id, e] of overlays) {
      if (!seen.has(id)) {
        e.overlay.remove();
        overlays.delete(id);
      }
    }
  };
  const schedule = () => {
    queue = queue.then(sync).catch((err) => console.warn('base layers', err));
  };
  schedule();
  const u1 = useStore.subscribe((s, p) => {
    if (s.baseLayers !== p.baseLayers || s.layerView !== p.layerView) schedule();
  });
  const u2 = useCalibration.subscribe(schedule);
  return () => {
    disposed = true;
    u1();
    u2();
    overlays.forEach((e) => e.overlay.remove());
  };
}

// ── chunk grid + 100 m grid ─────────────────────────────────────────────────────────────────

function gridController(lmap: L.Map): Dispose {
  const chunks = L.layerGroup();
  const minor = L.layerGroup();
  const noSnap = { pane: 'grid', interactive: false, pmIgnore: true, snapIgnore: true };

  const build = () => {
    chunks.clearLayers();
    minor.clearLayers();
    const map = getState().maps.find((m) => m.id === getState().mapId);
    if (!map) return;
    const origin = map.origin ?? { x: map.worldRect.minX, z: map.worldRect.minZ };
    for (const c of map.chunks ?? []) {
      const x0 = origin.x + c.cx * map.chunkSize;
      const z0 = origin.z + c.cz * map.chunkSize;
      L.rectangle(L.latLngBounds(toLatLng(x0, z0), toLatLng(x0 + map.chunkSize, z0 + map.chunkSize)), {
        ...noSnap,
        color: '#ffffff',
        weight: 1,
        opacity: 0.5,
        fill: false,
        dashArray: '6 6',
      } as L.PathOptions).addTo(chunks);
      // Label in the chunk's north-west corner so it doesn't sit on top of zone labels.
      L.tooltip({
        permanent: true,
        direction: 'right',
        className: 'chunk-lbl',
        offset: [2, 10],
        pane: 'grid',
      })
        .setLatLng(toLatLng(x0, z0 + map.chunkSize))
        .setContent(c.sceneName)
        .addTo(chunks);
    }
    const r = map.worldRect;
    const step = 100;
    for (let x = Math.ceil(r.minX / step) * step; x <= r.maxX; x += step) {
      L.polyline([toLatLng(x, r.minZ), toLatLng(x, r.maxZ)], {
        ...noSnap,
        color: '#ffffff',
        weight: x === 0 ? 1.5 : 0.6,
        opacity: x === 0 ? 0.55 : 0.25,
      } as L.PolylineOptions).addTo(minor);
    }
    for (let z = Math.ceil(r.minZ / step) * step; z <= r.maxZ; z += step) {
      L.polyline([toLatLng(r.minX, z), toLatLng(r.maxX, z)], {
        ...noSnap,
        color: '#ffffff',
        weight: z === 0 ? 1.5 : 0.6,
        opacity: z === 0 ? 0.55 : 0.25,
      } as L.PolylineOptions).addTo(minor);
    }
  };
  const apply = () => {
    const o = getState().overlays;
    if (o.chunkGrid) chunks.addTo(lmap);
    else chunks.remove();
    if (o.grid100) minor.addTo(lmap);
    else minor.remove();
  };
  build();
  apply();
  let lastChunks = JSON.stringify(getState().maps.find((m) => m.id === getState().mapId)?.chunks);
  const unsub = useStore.subscribe((s, p) => {
    if (s.overlays !== p.overlays) apply();
    if (s.maps !== p.maps) {
      const now = JSON.stringify(s.maps.find((m) => m.id === s.mapId)?.chunks);
      if (now !== lastChunks) {
        lastChunks = now;
        build();
      }
    }
  });
  return () => {
    unsub();
    chunks.remove();
    minor.remove();
  };
}

// ── features ────────────────────────────────────────────────────────────────────────────────

function paneFor(f: FeatureRecord): string {
  if (f.geometry.type === 'polyline') return 'paths';
  return f.type === 'zone' ? 'zones' : 'areas';
}

function debounce<A extends unknown[]>(fn: (...a: A) => void, ms: number) {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...a: A) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}

const POINT_LABEL_TYPES = new Set(['poi', 'npc', 'quest', 'player_start', 'respawn_point']);

function featuresController(lmap: L.Map): Dispose {
  const entries = new Map<string, { layer: L.Layer; sig: string }>();
  const routes = L.layerGroup().addTo(lmap);
  const savingWhileEditing = new Set<string>();

  const remove = (id: string) => {
    const e = entries.get(id);
    if (!e) return;
    (e.layer as PmLayer).pm?.disable();
    e.layer.remove();
    entries.delete(id);
  };

  const enableEdit = (layer: L.Layer, f: FeatureRecord) => {
    const pmLayer = layer as PmLayer;
    if (!pmLayer.pm) return;
    pmLayer.pm.enable({ allowSelfIntersection: false, snappable: true, snapDistance: 12, draggable: true });
    const save = debounce(async () => {
      const current = getState().features[f.id];
      const g = geometryFromLayer(layer, current?.geometry);
      if (!g) return;
      if (g.type === 'point') {
        const y = sampleHeight(g.x, g.z);
        if (y !== null) g.y = Math.round(y * 100) / 100;
      }
      savingWhileEditing.add(f.id);
      try {
        await updateFeature(f.id, { geometry: g });
      } finally {
        savingWhileEditing.delete(f.id);
      }
    }, 500);
    layer.on('pm:edit', save);
    layer.on('pm:dragend', save);
  };

  const sync = () => {
    const s = getState();
    const seen = new Set<string>();
    const labels = s.overlays.labels;
    for (const f of Object.values(s.features)) {
      if (!featureVisible(f, s) || f.geometry.type === 'none') continue;
      seen.add(f.id);
      const selected = f.id === s.selectedId;
      const editing = f.id === s.editingShapeId;
      const stepNo = f.type === 'quest_step' ? Number(f.properties.stepIndex) || undefined : undefined;
      const sig = `${f.rev}|${f.updated}|${selected ? 1 : 0}|${editing ? 1 : 0}|${labels ? 1 : 0}|${stepNo ?? ''}`;
      const existing = entries.get(f.id);
      if (existing && existing.sig === sig) continue;
      // Our own save during a vertex-edit session: keep the live layer, don't restart editing.
      if (existing && editing && existing.sig.split('|')[3] === '1' && savingWhileEditing.has(f.id)) {
        existing.sig = sig;
        continue;
      }
      remove(f.id);
      const layer = buildLayer(f, selected, paneFor(f), stepNo);
      if (!layer) continue;
      layer.on('click', (ev: L.LeafletMouseEvent) => {
        if (getState().tool.kind !== 'none') return; // let tools receive the click
        L.DomEvent.stopPropagation(ev);
        setState({ selectedId: f.id, editingShapeId: getState().editingShapeId === f.id ? f.id : null });
      });
      const showPermanent =
        labels && (f.type === 'zone' || (POINT_LABEL_TYPES.has(f.type) && f.geometry.type === 'point'));
      (layer as L.Path).bindTooltip(f.name, {
        permanent: showPermanent,
        direction: f.type === 'zone' ? 'center' : 'right',
        className: showPermanent ? (f.type === 'zone' ? 'lbl lbl-zone' : 'lbl lbl-pt') : 'lbl-hover',
        interactive: false,
      });
      layer.addTo(lmap);
      if (editing) enableEdit(layer, f);
      entries.set(f.id, { layer, sig });
    }
    for (const id of [...entries.keys()]) if (!seen.has(id)) remove(id);
    drawRoutes();
  };

  const drawRoutes = () => {
    routes.clearLayers();
    const s = getState();
    if (!s.overlays.questRoutes) return;
    const all = Object.values(s.features);
    for (const q of all) {
      if (q.type !== 'quest' || !featureVisible(q, s)) continue;
      const steps = all
        .filter((f) => f.parent === q.id && f.type === 'quest_step' && featureVisible(f, s))
        .sort((a, b) => (Number(a.properties.stepIndex) || 0) - (Number(b.properties.stepIndex) || 0));
      const pts: XZ[] = [];
      const qa = geometryAnchor(q.geometry);
      if (qa) pts.push([qa.x, qa.z]);
      for (const st of steps) {
        const a = geometryAnchor(st.geometry);
        if (a) pts.push([a.x, a.z]);
      }
      if (pts.length < 2) continue;
      const selected = s.selectedId === q.id || steps.some((st) => st.id === s.selectedId);
      L.polyline(pts.map(xzToLatLng), {
        pane: 'paths',
        color: '#f39c12',
        weight: selected ? 3 : 2,
        opacity: selected ? 0.95 : 0.55,
        dashArray: '2 8',
        lineCap: 'round',
        interactive: false,
        pmIgnore: true,
        snapIgnore: true,
      } as L.PolylineOptions).addTo(routes);
    }
  };

  sync();
  const unsub = useStore.subscribe((s, p) => {
    if (
      s.features !== p.features ||
      s.selectedId !== p.selectedId ||
      s.editingShapeId !== p.editingShapeId ||
      s.filters !== p.filters ||
      s.overlays !== p.overlays ||
      s.user !== p.user
    )
      sync();
  });
  return () => {
    unsub();
    for (const id of [...entries.keys()]) remove(id);
    routes.remove();
  };
}

// ── Unity as-built ghost layer ────────────────────────────────────────────────────────────

function ghostLayer(g: Geometry, name: string): L.Layer | null {
  const style = {
    pane: 'asbuilt',
    color: '#00e5ff',
    weight: 1.5,
    opacity: 0.9,
    dashArray: '4 4',
    fill: false,
    interactive: false,
    pmIgnore: true,
    snapIgnore: true,
  } as L.PathOptions;
  let layer: L.Layer | null = null;
  switch (g.type) {
    case 'point':
      layer = L.circleMarker(toLatLng(g.x, g.z), { ...style, radius: 5, dashArray: undefined });
      break;
    case 'circle':
      layer = L.circle(toLatLng(g.x, g.z), { ...style, radius: g.r });
      break;
    case 'rect':
      layer = L.rectangle(
        L.latLngBounds(toLatLng(g.x - g.w / 2, g.z - g.d / 2), toLatLng(g.x + g.w / 2, g.z + g.d / 2)),
        style,
      );
      break;
    case 'polygon':
      layer = L.polygon(g.points.map(xzToLatLng), style);
      break;
    case 'polyline':
      layer = L.polyline(g.points.map(xzToLatLng), style);
      break;
  }
  (layer as L.Path | null)?.bindTooltip(name, { className: 'lbl-hover' });
  return layer;
}

function asBuiltController(lmap: L.Map): Dispose {
  const group = L.layerGroup();
  const build = () => {
    group.clearLayers();
    const s = getState();
    if (!s.overlays.asBuilt) {
      group.remove();
      return;
    }
    for (const uo of Object.values(s.unityObjects)) {
      if (uo.missing || !uo.geometry) continue;
      ghostLayer(uo.geometry, `${uo.name} (Unity)`)?.addTo(group);
    }
    group.addTo(lmap);
  };
  build();
  const unsub = useStore.subscribe((s, p) => {
    if (s.unityObjects !== p.unityObjects || s.overlays !== p.overlays) build();
  });
  return () => {
    unsub();
    group.remove();
  };
}

// ── tools: draw, measure, pick, calibrate + cursor readout ─────────────────────────────────

function toolsController(lmap: L.Map): Dispose {
  const pmMap = lmap as PmMap;
  pmMap.pm.setGlobalOptions({
    snappable: true,
    snapDistance: 12,
    allowSelfIntersection: false,
    // Geoman's placement cursor + placed marker; replaced by our own marker as soon as it's saved.
    markerStyle: { icon: L.divIcon({ className: 'draw-cursor', iconSize: [12, 12], iconAnchor: [6, 6] }) },
  });
  const measureLayer = L.layerGroup().addTo(lmap);
  let measurePts: L.LatLng[] = [];
  let measureLine: L.Polyline | null = null;

  const resetMeasure = () => {
    measurePts = [];
    measureLayer.clearLayers();
    measureLine = null;
    useMeasure.setState({ length: 0, points: 0, area: null });
  };
  const updateMeasure = (hover?: L.LatLng) => {
    const pts = hover ? [...measurePts, hover] : measurePts;
    let len = 0;
    for (let i = 1; i < pts.length; i++) len += lmap.distance(pts[i - 1], pts[i]);
    const xz = measurePts.map((p) => [p.lng, p.lat] as XZ);
    useMeasure.setState({
      length: len,
      points: measurePts.length,
      area: xz.length >= 3 ? polygonArea(xz) : null,
    });
    if (!measureLine) {
      measureLine = L.polyline(pts, {
        color: '#ffeb3b',
        weight: 2,
        dashArray: '6 4',
        interactive: false,
        pmIgnore: true,
      } as L.PolylineOptions).addTo(measureLayer);
    } else measureLine.setLatLngs(pts);
  };

  const applyTool = () => {
    const tool = getState().tool;
    pmMap.pm.disableDraw();
    lmap.getContainer().classList.toggle('tool-active', tool.kind !== 'none');
    if (tool.kind !== 'measure') resetMeasure();
    if (tool.kind === 'draw') {
      const shape = geomanShape(tool.geometry);
      if (shape) pmMap.pm.enableDraw(shape, { snappable: true, snapDistance: 12, continueDrawing: false });
    }
  };

  const onCreate = async (e: { layer: L.Layer }) => {
    const tool = getState().tool;
    const g = geometryFromLayer(e.layer);
    e.layer.remove();
    setState({ tool: { kind: 'none' } });
    if (tool.kind !== 'draw' || !g) return;
    if (g.type === 'point') {
      const y = sampleHeight(g.x, g.z);
      if (y !== null) g.y = Math.round(y * 100) / 100;
    }
    await createFromPreset(tool.presetId, g);
  };

  const onClick = (e: L.LeafletMouseEvent) => {
    const s = getState();
    const x = e.latlng.lng,
      z = e.latlng.lat;
    switch (s.tool.kind) {
      case 'none':
        setState({ selectedId: null, editingShapeId: null });
        break;
      case 'measure':
        measurePts.push(e.latlng);
        L.circleMarker(e.latlng, {
          radius: 3,
          color: '#ffeb3b',
          interactive: false,
          pmIgnore: true,
        } as L.CircleMarkerOptions).addTo(measureLayer);
        updateMeasure();
        break;
      case 'pick': {
        const y = sampleHeight(x, z) ?? 0;
        const text = unityInspectorVector3(x, y, z);
        void navigator.clipboard?.writeText(text).then(
          () => toast(i18n.t('tools.copiedValue', { value: text })),
          () => toast(text),
        );
        break;
      }
      case 'calibrate': {
        const tool = s.tool;
        const cal = useCalibration.getState();
        const layer = s.baseLayers.find((l) => l.id === tool.layerId);
        if (!layer || !cal.draft) return;
        if (tool.step === 'image') {
          // Where on the IMAGE did they click? Invert the current (draft) calibration.
          const p = worldToPixel(cal.draft, x, z);
          setState({ tool: { ...tool, step: 'world', pending: { px: p.px, py: p.py } } });
        } else if (tool.pending) {
          cal.set({
            points: [
              ...cal.points,
              {
                px: Math.round(tool.pending.px * 10) / 10,
                py: Math.round(tool.pending.py * 10) / 10,
                x: Math.round(x * 100) / 100,
                z: Math.round(z * 100) / 100,
              },
            ],
          });
          setState({ tool: { kind: 'calibrate', layerId: tool.layerId, step: 'image' } });
        }
        break;
      }
    }
  };

  const onMove = (e: L.LeafletMouseEvent) => {
    useCursor.setState({ x: e.latlng.lng, z: e.latlng.lat, inside: true });
    if (getState().tool.kind === 'measure' && measurePts.length) updateMeasure(e.latlng);
  };
  const onOut = () => useCursor.setState({ inside: false });

  const onKey = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    if (e.key === 'Escape') {
      const s = getState();
      if (s.tool.kind === 'measure' && measurePts.length) resetMeasure();
      else if (s.tool.kind !== 'none') setState({ tool: { kind: 'none' } });
      else if (s.editingShapeId) setState({ editingShapeId: null });
      else setState({ selectedId: null });
    }
  };

  lmap.on('pm:create', onCreate as unknown as L.LeafletEventHandlerFn);
  lmap.on('click', onClick);
  lmap.on('mousemove', onMove);
  lmap.on('mouseout', onOut);
  window.addEventListener('keydown', onKey);
  applyTool();
  const unsub = useStore.subscribe((s, p) => {
    if (s.tool.kind !== p.tool.kind || (s.tool.kind === 'draw' && s.tool !== p.tool)) applyTool();
  });
  return () => {
    unsub();
    lmap.off('pm:create', onCreate as unknown as L.LeafletEventHandlerFn);
    lmap.off('click', onClick);
    lmap.off('mousemove', onMove);
    lmap.off('mouseout', onOut);
    window.removeEventListener('keydown', onKey);
    pmMap.pm.disableDraw();
    measureLayer.remove();
  };
}

function flyToController(lmap: L.Map): Dispose {
  return useStore.subscribe((s, p) => {
    if (!s.flyTo || s.flyTo === p.flyTo) return;
    const zoom = s.flyTo.zoom ?? Math.max(lmap.getZoom(), 1);
    lmap.flyTo(toLatLng(s.flyTo.x, s.flyTo.z), zoom, { duration: 0.6 });
  });
}

export function createMapControllers(lmap: L.Map): Dispose {
  const disposers = [
    baseLayersController(lmap),
    gridController(lmap),
    featuresController(lmap),
    asBuiltController(lmap),
    toolsController(lmap),
    flyToController(lmap),
  ];
  return () => disposers.forEach((d) => d());
}
